"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState, useEffect, useMemo } from "react";
import { Calendar, MapPin, Trophy, Users, Euro, Maximize, Layers, LayoutTemplate, Clock, Image as ImageIcon, ExternalLink, Handshake, ArrowRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
function TournamentsGridConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    loadMore,
    isRefetching
  } = usePosts();
  const [resolvedLogos, setResolvedLogos] = useState<Record<string, string>>({});
  const now = new Date();
  now.setHours(0, 0, 0, 0);

  // Filtrage et tri des tournois à venir (et en direct)
  const upcomingPosts = useMemo(() => {
    return posts.map(post => {
      const fields = post.customFields || {};
      const tDate = fields.date ? new Date(fields.date) : null;
      let autoStatus = "À venir";
      if (tDate) {
        const compareDate = new Date(tDate);
        compareDate.setHours(0, 0, 0, 0);
        if (compareDate.getTime() === now.getTime()) {
          autoStatus = "En Direct";
        }
      }
      return {
        ...post,
        tournamentName: fields.name || post.title,
        tournamentDate: tDate,
        tournamentLocation: fields.location || "Lieu non précisé",
        autoStatus
      };
    }).filter(post => {
      if (!post.tournamentDate) return false;
      const compareDate = new Date(post.tournamentDate);
      compareDate.setHours(0, 0, 0, 0);
      // On garde uniquement les dates supérieures ou égales à aujourd'hui
      return compareDate.getTime() >= now.getTime();
    }).sort((a, b) => {
      // Tri croissant (le tournoi le plus proche apparaît en premier)
      return (a.tournamentDate?.getTime() || 0) - (b.tournamentDate?.getTime() || 0);
    });
  }, [posts]);

  // Helper pour résoudre l'image du logo du club partenaire
  const resolveImage = async (val: any) => {
    const WP_URL = "https://settled-gobbler.10web.cloud";
    if (!val || val === "") return null;
    if (typeof val === 'string' && val.startsWith('http')) return val;
    if (typeof val === 'number' || typeof val === 'string' && /^\d+$/.test(val)) {
      try {
        const response = await fetch(`${WP_URL}/wp-json/wp/v2/media/${val}`);
        if (!response.ok) return null;
        const data = await response.json();
        return data.source_url || null;
      } catch (e) {
        return null;
      }
    }
    return null;
  };
  useEffect(() => {
    const resolveAllLogos = async () => {
      const newResolved: Record<string, string> = {};
      for (const post of upcomingPosts) {
        if (resolvedLogos[post.id]) continue;
        const fields = post.customFields || {};
        const partner = await resolveImage(fields.partner_club_logo);
        if (partner) {
          newResolved[post.id] = partner;
        }
      }
      if (Object.keys(newResolved).length > 0) {
        setResolvedLogos(prev => ({
          ...prev,
          ...newResolved
        }));
      }
    };
    if (upcomingPosts.length > 0) resolveAllLogos();
  }, [upcomingPosts]);
  if (loading && posts.length === 0) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => <Card key={i} className="h-[450px] animate-pulse rounded-lg border-none bg-[#1a1d24]/50" />)}
      </div>;
  }
  if (error || upcomingPosts.length === 0) {
    return <div className="flex flex-col items-center justify-center py-24 text-center">
        <Trophy className="mb-4 size-12 text-muted-foreground/50" />
        <p className="text-lg text-muted-foreground">Aucun tournoi à venir pour le moment.</p>
      </div>;
  }
  return <div className="space-y-12">
      <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {upcomingPosts.map((post, i) => {
        const postFields = post.customFields || {};
        const displayDate = post.tournamentDate ? post.tournamentDate.toLocaleDateString('fr-FR', {
          day: 'numeric',
          month: 'long',
          year: 'numeric'
        }) : "Date à confirmer";
        const partnerLogo = resolvedLogos[post.id];
        return <div key={post.id} className="h-full" data-index={i}>
              <Card className="group h-full flex flex-col overflow-hidden border-none bg-[#1a1d24] shadow-lg transition-all duration-300 hover:-translate-y-2 rounded-lg text-white py-0">
                
                {/* Badges de statut et type */}
                <CardHeader className="p-0 relative">
                  <div className="absolute top-4 left-4 flex flex-wrap gap-2">
                    <Badge className={`font-default uppercase tracking-wider border-none shadow-md px-3 py-1 text-[10px] ${post.autoStatus === 'En Direct' ? 'bg-red-600 text-white animate-pulse' : 'bg-green-600 text-white'}`}>
                      {post.autoStatus}
                    </Badge>

                    {postFields.tournament_type && <Badge className="bg-blue-700 text-white font-default uppercase tracking-wider border-none shadow-md px-3 py-1 text-[10px]">
                        {postFields.tournament_type}
                      </Badge>}
                    
                    {postFields.has_pro_teams === "1" && <Badge className="bg-amber-600 text-white font-default uppercase tracking-wider border-none shadow-md px-3 py-1 text-[10px]">
                        Élite
                      </Badge>}
                  </div>
                </CardHeader>

                {/* Contenu de la carte */}
                <CardContent className="flex-grow px-4 pt-5 flex flex-col gap-3">
                  <CardTitle className="text-2xl font-bold text-[#f1f3f5] uppercase tracking-tight leading-tight">
                    {post.tournamentName}
                  </CardTitle>

                  <div className="flex items-center gap-2 text-xs text-[#ffcc00] font-medium uppercase tracking-wider">
                    <SafeHydrate><Calendar className="size-3.5" /></SafeHydrate>
                    <span>{displayDate}</span>
                  </div>
                  
                  <div className="flex items-center gap-2 text-xs text-[#a0aec0] font-medium">
                    <MapPin className="size-3.5" />
                    <span className="line-clamp-1">{post.tournamentLocation}</span>
                  </div>

                  {/* {postFields.card_description && (
                    <CardDescription className="text-sm text-[#a0aec0] font-default leading-relaxed">
                      {postFields.card_description}
                    </CardDescription>
                   )} */}

                  {postFields.card_description && <CardDescription className="text-sm text-[#a0aec0] font-default leading-relaxed whitespace-pre-line">
                      {postFields.card_description}
                    </CardDescription>}

                  <div className="flex flex-col gap-2 py-2">
                    <div className="flex flex-col gap-1.5 mt-1">
                      {postFields.wvc_tournoi_age_categories && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Users className="size-3.5 text-[#ffcc00]/70" />
                          <div className="font-medium">Catégorie(s) :</div>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_age_categories}
                          </Badge>
                        </div>}
                      
                      {postFields.wvc_tournoi_game_format && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <LayoutTemplate className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Format :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_game_format}
                          </Badge>
                        </div>}

                      {postFields.max_players && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Users className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Joueurs :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.max_players}
                          </Badge>
                        </div>}

                      {postFields.wvc_tournoi_match_duration && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Clock className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Durée :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_match_duration}
                          </Badge>
                        </div>}

                      {postFields.wvc_tournoi_pitch_type && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Layers className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Terrain :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_pitch_type}
                          </Badge>
                        </div>}

                      {postFields.matchs_garantis && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Trophy className="size-3.5 text-[#ffcc00]/70" />
                          <div className="font-medium">Matchs garantis :</div>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.matchs_garantis}
                          </Badge>
                        </div>}

                      {postFields.wvc_tournoi_max_teams && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Maximize className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Nombre d'équipes :</span>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_max_teams}
                          </Badge>
                        </div>}

                      {postFields.partner_club_name && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Handshake className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Club partenaire :</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                              {postFields.partner_club_name}
                            </Badge>
                            {partnerLogo && <img src={partnerLogo} alt={postFields.partner_club_name} className="w-[25px] h-[25px] object-contain" loading="lazy" />}
                          </div>
                        </div>}

                      {postFields.wvc_tournoi_registration_fee && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <Euro className="size-3.5 text-[#ffcc00]/70" />
                          <div className="font-medium">Frais d'inscription :</div>
                          <Badge variant="outline" className="h-5 px-1.5 text-[12px] border-[#ffcc00]/30 text-[#ffcc00] bg-[#ffcc00]/5">
                            {postFields.wvc_tournoi_registration_fee}
                          </Badge>
                        </div>} 

                      {postFields.gallery_url && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <ImageIcon className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Galerie :</span>
                          <Link to={postFields.gallery_url} newTab className="text-[#ffcc00] hover:underline">
                            Voir les photos
                          </Link>
                        </div>}

                      {postFields.lien_vers_resultats && <div className="flex items-center gap-2 text-xs text-[#a0aec0]">
                          <ExternalLink className="size-3.5 text-[#ffcc00]/70" />
                          <span className="font-medium">Résultats :</span>
                          <Link to={postFields.lien_vers_resultats} newTab className="text-[#ffcc00] hover:underline">
                            Consulter
                          </Link>
                        </div>}
                    </div>
                  </div>
                </CardContent>

                {/* Bouton d'action pour l'inscription */}
                {/* <CardFooter className="p-0 my-6">
                  <Button asChild className="w-3/4 mx-auto bg-[#ffcc00] hover:bg-[#e6b800] text-[#121417] font-black uppercase py-5 rounded-xl transition-all duration-200 shadow-md">
                    <Link to={post.link || "#"}>
                      S'inscrire
                      <ArrowRight className="size-4 ml-2" />
                    </Link>
                  </Button>
                 </CardFooter> */}

                {/* Bouton d'action pour l'inscription Google Form */}
                <CardFooter className="p-0 my-0 mb-4">
                  <Button asChild className="w-3/4 mx-auto bg-[#ffcc00] hover:bg-[#e6b800] text-[#121417] font-black uppercase py-3 rounded-xl transition-all duration-200 shadow-md">
                    <Link to={postFields.payment_link} newTab={true}>
                      S'inscrire
                      <ArrowRight className="size-4 ml-2" />
                    </Link>
                  </Button>
                </CardFooter>

              </Card>
            </div>;
      })}
      </div>

      {hasNext && <div className="flex justify-center pt-8">
          <Button variant="outline" onClick={async () => await loadMore()} disabled={isRefetching} className="rounded-lg border-2 border-[#ffcc00] bg-transparent px-8 py-6 font-bold uppercase tracking-wider text-[#ffcc00] hover:bg-[#ffcc00] hover:text-[#121417]">
            {isRefetching ? "Chargement..." : "Voir plus de tournois"}
          </Button>
        </div>}
    </div>;
}
export default function UpcomingTournaments() {
  return <section id="upcoming-tournaments" className="bg-background text-foreground py-12 sm:py-12">
      {/* <div className="container mx-auto px-4 md:px-6"> */}
      <div className="max-w-[1600px] mx-auto px-4 md:px-6">
        <div className="flex flex-col items-center text-center mb-16 space-y-4">
          {/* <Badge variant="outline" className="px-3 py-1 text-sm border-primary/30 bg-primary/10 text-primary font-default uppercase tracking-widest">
            Calendrier Officiel
           </Badge> */}
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-foreground tracking-tighter uppercase">
            Prochains Tournois
          </h2>
          <div className="mt-4 h-1 w-24 bg-[#ffcc00]"></div>
        </div>

        <PostsProvider wp_query={{
        post_type: "tournoi",
        per_page: -1,
        order: "asc",
        orderby: "date"
      }}>
          <TournamentsGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}